'use client'

import { memo } from 'react'
import { CRAFT_MEDIA } from './craft-media'

/** baby: Peek angles: same idea as NinxlCapabilityAssetMock on classic /ninxl. */
type CraftStampPlacement =
  | 'right-mid-cw'
  | 'right-high-ccw'
  | 'corner-bottom-cw'
  | 'left-mid-ccw'
  | 'left-high-cw'
  | 'right-low-ccw'

const USE_CASES = [
  {
    id: 'studio',
    title: 'Studio workflow agents',
    body: 'Explainers, comics, trailers, briefs — pick a kit and send',
    icon: CRAFT_MEDIA.icons.studio,
    placement: 'right-mid-cw' as const,
  },
  {
    id: 'dictation',
    title: 'System-wide dictation',
    body: 'Speak anywhere on your Mac',
    icon: CRAFT_MEDIA.icons.mic,
    placement: 'right-high-ccw' as const,
  },
  {
    id: 'screen',
    title: 'AI screen reply',
    body: 'Bench reply from what is on screen',
    icon: CRAFT_MEDIA.icons.screen,
    placement: 'corner-bottom-cw' as const,
  },
  {
    id: 'agents',
    title: 'Coding agents',
    body: 'Run Codex, Claude Code & more',
    icon: CRAFT_MEDIA.icons.code,
    placement: 'left-mid-ccw' as const,
  },
  {
    id: 'automations',
    title: 'Automations',
    body: 'Schedule and run your agentic tasks on repeat',
    icon: CRAFT_MEDIA.icons.automations,
    placement: 'left-high-cw' as const,
  },
  {
    id: 'rooms',
    title: 'Rooms',
    body: 'Team agents & shared compute',
    icon: CRAFT_MEDIA.icons.rooms,
    placement: 'right-low-ccw' as const,
  },
] as const satisfies ReadonlyArray<{
  id: string
  title: string
  body: string
  icon: string
  placement: CraftStampPlacement
}>

/**
 * baby: Product Hunt craft board: large icons overflow ~30% like capability tiles.
 */
export const CraftUseCases = memo(function CraftUseCases() {
  return (
    <section id="studio" className="craft-board" aria-labelledby="craft-studio-title">
      <div className="craft-board__mat">
        <img
          className="craft-board__edge craft-board__edge--tl"
          src={CRAFT_MEDIA.stickers.star}
          alt=""
          aria-hidden
        />
        <img
          className="craft-board__edge craft-board__edge--br"
          src={CRAFT_MEDIA.stickers.kraft}
          alt=""
          aria-hidden
        />

        <header className="craft-board__header craft-reveal">
          <p className="craft-section__eyebrow">What you can do</p>
          <h2 id="craft-studio-title" className="craft-section__title">
            What you can do with NINX
          </h2>
          <p className="craft-section__body">
            Studio kits, dictation, automations, rooms, and more — on your machine, not a cloud dashboard.
          </p>
        </header>

        <div className="craft-board__grid">
          {USE_CASES.map((item, index) => (
            <article
              key={item.id}
              className={`craft-stamp craft-stamp--group craft-reveal craft-reveal--d${(index % 3) + 1}`}
              data-placement={item.placement}
            >
              <span className="craft-stamp__tape craft-stamp__tape--top" aria-hidden />
              <span className="craft-stamp__tape craft-stamp__tape--bot" aria-hidden />

              {/* baby: Oversized craft icon peeks past the card edge (~30% overflow). */}
              <div className="craft-stamp__asset" aria-hidden>
                <img
                  className={`craft-stamp__asset-img craft-stamp__asset-img--${item.placement}`}
                  src={item.icon}
                  alt=""
                  draggable={false}
                />
              </div>

              <div className="craft-stamp__copy">
                <h3 className="craft-stamp__title">{item.title}</h3>
                <p className="craft-stamp__body">{item.body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
})

CraftUseCases.displayName = 'CraftUseCases'
